import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, List, ListItem, Typography } from '@mui/material';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { ReactComponent as ChainsIcon } from 'assets/icons/ChainsIcon.svg';
import { ReactComponent as CheckCircleGreen } from 'assets/icons/CheckCircleGreen.svg';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/CheckmarkIcon.svg';
import { ReactComponent as ExclamationCircleIcon } from 'assets/icons/ExclamationCircleIcon.svg';
import { ReactComponent as Visibility } from 'assets/icons/Visibility.svg';

import { Button } from 'components/_basic/Button/Button';
import { Modal } from 'components/_basic/Modal';

import { copyPdfLink } from 'helpers/viewDocHandler';

import { IPDFStatusModalButton, IPDFUploadStatus, IPDFStatusModalProps as IProps, PDF_UPLOAD_STATUS } from './types';

import styles from './PDFStatusModal.module.scss';

const modalIcon = new Map<IPDFUploadStatus, JSX.Element>([
  [PDF_UPLOAD_STATUS.SUCCESS, <CheckCircleGreen />],
  [PDF_UPLOAD_STATUS.FAIL, <ExclamationCircleIcon />],
]);

export const PDFStatusModal: FC<IProps> = ({ onClose, onFileUpload, onViewDoc, status }) => {
  const { t } = useTranslation();
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const { uuid } = useBusinessToolStore(getBusinessPlan);

  const copyClickHandler = () => {
    copyPdfLink(uuid);
    setIsLinkCopied(true);
  };

  const successButtons: IPDFStatusModalButton[] = [
    {
      title: t(isLinkCopied ? 'copiedLink' : 'copyLink'),
      icon: isLinkCopied ? <CheckmarkIcon /> : <ChainsIcon />,
      clickHandler: copyClickHandler,
    },
    { title: t('viewDocument'), icon: <Visibility />, clickHandler: onViewDoc },
  ];

  const failureButtons: IPDFStatusModalButton[] = [
    { title: t('cancel'), clickHandler: onClose },
    { title: t('tryAgain'), variant: 'contained', clickHandler: onFileUpload },
  ];

  const modalTitle = new Map<IPDFUploadStatus, string>([
    [PDF_UPLOAD_STATUS.SUCCESS, t('onlinePDFIsReady')],
    [PDF_UPLOAD_STATUS.FAIL, t('onlinePDFFailed')],
  ]);

  const buttons = new Map<IPDFUploadStatus, IPDFStatusModalButton[]>([
    [PDF_UPLOAD_STATUS.SUCCESS, successButtons],
    [PDF_UPLOAD_STATUS.FAIL, failureButtons],
  ]);

  return (
    // TODO: refactor styles (width+height)
    <Modal close={onClose} showCloseButton={false}>
      <Box className={styles.modalWrapper}>
        <Box className={styles.modalIcon}>{modalIcon.get(status)}</Box>
        <Box className={styles.textWrapper}>
          <Typography className={styles.modalTitle} variant="h3">
            {modalTitle.get(status)}
          </Typography>
          {status === PDF_UPLOAD_STATUS.FAIL && (
            <Typography className={styles.modalText} variant="body1">
              {t('sorrySomethingWentWrong')}
            </Typography>
          )}
        </Box>
        <List className={styles.buttonList}>
          {buttons.get(status)?.map(({ title, icon, clickHandler, variant = 'outlined' }) => (
            <ListItem key={title} className={styles.listItem}>
              <Button fullWidth variant={variant} endIcon={icon} onClick={clickHandler}>
                {title}
              </Button>
            </ListItem>
          ))}
        </List>
      </Box>
    </Modal>
  );
};
